import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import axios from "axios";
import { Phone, MapPin, Clock, Star, Instagram, MessageCircle, ChevronRight, Leaf, Heart, Award, Truck, Calendar, CreditCard, Menu, X, Mail, Sparkles } from "lucide-react";
import { Toaster, toast } from "sonner";
import MenuPage from "./pages/MenuPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navbar Component
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", href: "#home" },
    { name: "Menu", href: "/menu", isPage: true },
    { name: "About", href: "#about" },
    { name: "Reviews", href: "#reviews" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-white/95 nav-blur shadow-md py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between">
        <a href="#home" className="flex items-center gap-3 logo-3d" data-testid="logo">
          <img 
            src="https://customer-assets.emergentagent.com/job_0414f390-949e-455d-be81-ddfd0c694b7a/artifacts/qfscq7gd_utsav_an_indian_bakery-20260301-0001.jpg" 
            alt="Utsav Bakery Logo" 
            className="w-12 h-12 rounded-full object-cover shadow-md border-2 border-[#FF9933]/30"
          />
          <span className="font-accent text-3xl text-[#FF9933]">Utsav</span>
        </a>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            link.isPage ? (
              <Link
                key={link.name}
                to={link.href}
                className="font-body text-[#2D2D2D] hover:text-[#FF9933] transition-colors font-medium relative group"
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#FF9933] transition-all duration-300 group-hover:w-full"></span>
              </Link>
            ) : (
              <a
                key={link.name}
                href={link.href}
                className="font-body text-[#2D2D2D] hover:text-[#FF9933] transition-colors font-medium relative group"
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#FF9933] transition-all duration-300 group-hover:w-full"></span>
              </a>
            )
          ))}
          <a
            href="https://wa.me/31685177049"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-whatsapp animate-pulse-soft"
            data-testid="nav-whatsapp-btn"
          >
            <MessageCircle size={18} />
            Order Now
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          data-testid="mobile-menu-btn"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white shadow-lg py-4 px-4 animate-fade-in-up" data-testid="mobile-menu">
          {navLinks.map((link) => (
            link.isPage ? (
              <Link
                key={link.name}
                to={link.href}
                className="block py-3 font-body text-[#2D2D2D] hover:text-[#FF9933] border-b border-gray-100"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ) : (
              <a
                key={link.name}
                href={link.href}
                className="block py-3 font-body text-[#2D2D2D] hover:text-[#FF9933] border-b border-gray-100"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </a>
            )
          ))}
          <a
            href="https://wa.me/31685177049"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-whatsapp mt-4 justify-center w-full"
          >
            <MessageCircle size={18} />
            Order Now
          </a>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = ({ contact }) => {
  return (
    <section id="home" className="hero-section pattern-overlay pt-24 md:pt-0" data-testid="hero-section">
      {/* Floating decorations */}
      <div className="floating-decoration floating-decoration-1"></div>
      <div className="floating-decoration floating-decoration-2"></div>
      <div className="floating-decoration floating-decoration-3"></div>
      
      <div className="max-w-7xl mx-auto px-4 md:px-8 min-h-screen flex items-center relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center w-full py-12">
          {/* Left Content */}
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 badge-primary rounded-full animate-fade-in-up opacity-0" style={{animationDelay: '0.1s', animationFillMode: 'forwards'}}>
              <Star size={16} className="star-filled" fill="#FFD700" />
              <span>5.0 Rating • 160+ Reviews</span>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2D2D2D] leading-tight animate-fade-in-up opacity-0" style={{animationDelay: '0.2s', animationFillMode: 'forwards'}}>
              Freshly Baked with
              <span className="gradient-text block animate-gradient bg-gradient-to-r from-[#FF9933] via-[#B76E79] to-[#FF9933]">Love & Care</span>
            </h1>
            
            <p className="font-body text-base md:text-lg text-[#555555] max-w-lg animate-fade-in-up opacity-0" style={{animationDelay: '0.3s', animationFillMode: 'forwards'}}>
              Welcome to <span className="font-semibold text-[#FF9933]">Utsav - An Indian Bakery</span>. 
              We craft mouthwatering eggless cakes, cupcakes, and mousse with the finest ingredients, 
              bringing joy to every celebration.
            </p>

            <div className="flex flex-wrap gap-3 animate-fade-in-up opacity-0" style={{animationDelay: '0.4s', animationFillMode: 'forwards'}}>
              <span className="badge badge-secondary transform hover:scale-105 transition-transform cursor-default">
                <Leaf size={16} /> 100% Eggless
              </span>
              <span className="badge badge-accent transform hover:scale-105 transition-transform cursor-default">
                <Heart size={16} /> Women-Owned
              </span>
              <span className="badge badge-primary transform hover:scale-105 transition-transform cursor-default">
                <Award size={16} /> Freshly Baked
              </span>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4 animate-fade-in-up opacity-0" style={{animationDelay: '0.5s', animationFillMode: 'forwards'}}>
              <a
                href="https://wa.me/31685177049"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp text-center justify-center animate-pulse-soft"
                data-testid="hero-whatsapp-btn"
              >
                <MessageCircle size={20} />
                Order on WhatsApp
              </a>
              <Link
                to="/menu"
                className="btn-secondary text-center hover:scale-105 transition-transform"
                data-testid="hero-menu-btn"
              >
                Explore Menu
              </Link>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative animate-fade-in-right opacity-0" style={{animationDelay: '0.3s', animationFillMode: 'forwards'}}>
            <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl hero-image-3d glow-hover">
              <img
                src="https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=600&q=80"
                alt="Delicious cake from Utsav Bakery"
                className="w-full h-[400px] md:h-[500px] object-cover"
                data-testid="hero-image"
              />
            </div>
            {/* Decorative elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#FF9933]/30 rounded-full blur-xl animate-float"></div>
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#93C572]/30 rounded-full blur-xl animate-float" style={{animationDelay: '1s'}}></div>
            <div className="absolute top-1/2 -right-8 w-16 h-16 bg-[#B76E79]/30 rounded-full blur-lg animate-float" style={{animationDelay: '2s'}}></div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Menu Preview Section with Custom Cake CTA
const MenuSection = ({ menuItems }) => {
  // Show only first 8 items as preview - safely handle if menuItems is not an array
  const previewItems = (menuItems || []).slice(0, 8);

  return (
    <section id="menu" className="py-16 md:py-24 bg-white" data-testid="menu-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="section-header">
          <span className="font-accent text-3xl text-[#FF9933] animate-fade-in-up">Our Specialties</span>
          <h2 className="section-title mt-2 animate-fade-in-up" style={{animationDelay: '0.1s'}}>Delicious Menu</h2>
          <div className="section-divider mx-auto mt-4"></div>
          <p className="section-subtitle mt-4 animate-fade-in-up" style={{animationDelay: '0.2s'}}>
            Handcrafted with love, every item is 100% eggless and made fresh to order
          </p>
        </div>

        {/* Custom Cake CTA Banner */}
        <div className="bg-gradient-to-r from-[#FF9933] to-[#B76E79] rounded-2xl p-6 md:p-8 mb-12 animate-fade-in-up" style={{animationDelay: '0.3s'}} data-testid="custom-cake-cta">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left text-white">
              <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                <Sparkles size={24} />
                <span className="font-heading text-xl font-bold">CUSTOM CAKES AVAILABLE!</span>
              </div>
              <p className="text-white/90">
                Tell us your design, theme, or flavor - we'll create it for you! 🎨
              </p>
            </div>
            <a
              href="https://wa.me/31685177049?text=Hi! I want to order a CUSTOM cake. My requirements are:"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-[#FF9933] px-8 py-3 rounded-full font-bold flex items-center gap-2 hover:bg-gray-100 transition-colors shadow-lg whitespace-nowrap"
              data-testid="custom-cake-btn"
            >
              <MessageCircle size={20} />
              Order Custom Cake
            </a>
          </div>
        </div>

        {/* Menu Preview Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
          {previewItems.map((item, index) => (
            <div
              key={item.id}
              className="product-card relative animate-slide-in-scale opacity-0"
              data-testid={`menu-item-${item.name.toLowerCase().replace(/\s/g, '-')}`}
              style={{animationDelay: `${index * 0.1}s`, animationFillMode: 'forwards'}}
            >
              {item.is_exclusive && (
                <div className="exclusive-badge animate-bounce-in" style={{animationDelay: `${index * 0.1 + 0.3}s`}}>Exclusive</div>
              )}
              <div className="img-zoom overflow-hidden">
                <img
                  src={item.image_url}
                  alt={item.name}
                  className="h-[180px] md:h-[200px]"
                />
              </div>
              <div className="p-3 md:p-4">
                <h3 className="font-heading text-base md:text-lg font-semibold text-[#2D2D2D] line-clamp-1">
                  {item.name}
                </h3>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Link
            to="/menu"
            className="btn-primary inline-flex items-center gap-2 hover:scale-105 transition-transform"
            data-testid="view-full-menu-btn"
          >
            View Full Menu (50+ Items)
            <ChevronRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = ({ features }) => {
  const featuresList = [
    { icon: Leaf, title: "100% Eggless", desc: "All our products are completely eggless", color: "#93C572" },
    { icon: Heart, title: "Women-Owned", desc: "Proudly run by Chef Jayshree", color: "#B76E79" },
    { icon: Award, title: "Freshly Baked", desc: "Made fresh for every order", color: "#FF9933" },
    { icon: Truck, title: "Delivery Available", desc: "We deliver across Netherlands", color: "#93C572" },
    { icon: Calendar, title: "Theme Cakes", desc: "Order 7-10 days in advance", color: "#B76E79" },
    { icon: CreditCard, title: "Easy Payment", desc: "Cards & NFC payments accepted", color: "#FF9933" },
  ];

  return (
    <section id="about" className="py-16 md:py-24 bg-gradient-soft pattern-overlay" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Image - Owner with family */}
          <div className="relative animate-fade-in-left opacity-0" style={{animationDelay: '0.2s', animationFillMode: 'forwards'}}>
            <div className="rounded-2xl overflow-hidden shadow-xl glow-hover">
              <img
                src="https://customer-assets.emergentagent.com/job_0414f390-949e-455d-be81-ddfd0c694b7a/artifacts/0vkn43ky_CamScanner%2001-03-2026%2001.20%281%29_25.jpg"
                alt="Chef Jayshree with family - Utsav Bakery"
                className="w-full h-[400px] object-cover"
                data-testid="about-image"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-lg animate-bounce-in" style={{animationDelay: '0.5s'}}>
              <p className="font-accent text-2xl text-[#FF9933]">Since 2023</p>
              <p className="font-body text-sm text-[#555555]">Spreading Sweetness</p>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-6">
            <span className="font-accent text-3xl text-[#FF9933] animate-fade-in-up">Our Story</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#2D2D2D] animate-fade-in-up" style={{animationDelay: '0.1s'}}>
              Crafted with Passion & Love
            </h2>
            <div className="section-divider"></div>
            <p className="font-body text-[#555555] leading-relaxed animate-fade-in-up" style={{animationDelay: '0.2s'}}>
              <span className="font-semibold text-[#2D2D2D]">Utsav - An Indian Bakery</span> was born from a passion 
              for creating delicious, eggless treats that bring joy to every celebration. Founded by 
              <span className="font-semibold text-[#FF9933]"> Chef Jayshree</span> in Netherlands, 
              we specialize in handcrafted cakes, cupcakes, and mousse using only the finest ingredients.
            </p>
            <p className="font-body text-[#555555] leading-relaxed animate-fade-in-up" style={{animationDelay: '0.3s'}}>
              Our signature <span className="font-semibold">Paan Cake</span> and <span className="font-semibold">Rasmalai Cake</span> have become the talk of the town, 
              blending traditional Indian flavors with modern baking techniques. Every bite tells a story of 
              dedication, quality, and love.
            </p>

            {/* Features Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4">
              {featuresList.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-3 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105 animate-fade-in-up"
                  data-testid={`feature-${index}`}
                  style={{animationDelay: `${0.4 + index * 0.1}s`, animationFillMode: 'forwards', opacity: 0}}
                >
                  <feature.icon size={24} style={{ color: feature.color }} />
                  <div>
                    <h4 className="font-semibold text-sm text-[#2D2D2D]">{feature.title}</h4>
                    <p className="text-xs text-[#555555]">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Reviews Section
const ReviewsSection = ({ reviews }) => {
  const scrollRef = useRef(null);

  return (
    <section id="reviews" className="py-16 md:py-24 bg-white" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="section-header">
          <span className="font-accent text-3xl text-[#FF9933]">Testimonials</span>
          <h2 className="section-title mt-2">What Our Customers Say</h2>
          <div className="section-divider mx-auto mt-4"></div>
          <div className="flex items-center justify-center gap-2 mt-4">
            <div className="flex">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star key={star} size={24} className="star-filled" fill="#FFD700" />
              ))}
            </div>
            <span className="font-semibold text-[#2D2D2D]">5.0</span>
            <span className="text-[#555555]">• 160+ Reviews on Google</span>
          </div>
        </div>

        {/* Horizontal Scroll Reviews */}
        <div
          ref={scrollRef}
          className="horizontal-scroll gap-6 pb-4 mt-8"
          data-testid="reviews-scroll"
        >
          {(reviews || []).map((review, index) => (
            <div key={review.id} className="review-card" data-testid={`review-${index}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF9933] to-[#B76E79] flex items-center justify-center text-white font-bold text-lg">
                  {review.reviewer_name.charAt(0)}
                </div>
                <div>
                  <h4 className="font-semibold text-[#2D2D2D]">{review.reviewer_name}</h4>
                  <p className="text-sm text-[#555555]">{review.date}</p>
                </div>
              </div>
              <div className="flex mb-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} size={16} className="star-filled" fill="#FFD700" />
                ))}
              </div>
              <p className="text-[#555555] leading-relaxed text-sm line-clamp-4">
                "{review.review_text}"
              </p>
              <div className="flex gap-4 mt-4 pt-4 border-t border-gray-100 text-sm">
                <span className="text-[#93C572]">Food: {review.food_rating}/5</span>
                <span className="text-[#FF9933]">Service: {review.service_rating}/5</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = ({ contact }) => {
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  return (
    <section id="contact" className="py-16 md:py-24 bg-gradient-soft pattern-overlay" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="section-header">
          <span className="font-accent text-3xl text-[#FF9933]">Get in Touch</span>
          <h2 className="section-title mt-2">Contact Us</h2>
          <div className="section-divider mx-auto mt-4"></div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mt-12">
          {/* Contact Info */}
          <div className="contact-card">
            <h3 className="font-heading text-2xl font-bold text-[#2D2D2D] mb-6">
              Ready to Order?
            </h3>
            <p className="text-[#555555] mb-6">
              Simply WhatsApp or call us to place your order. For theme cakes, 
              please order at least 7-10 days in advance.
            </p>

            {/* WhatsApp CTA */}
            <a
              href="https://wa.me/31685177049?text=Hi! I would like to place an order from Utsav Bakery."
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp w-full justify-center text-lg mb-6 relative pulse-ring"
              data-testid="contact-whatsapp-btn"
            >
              <MessageCircle size={24} />
              Order on WhatsApp
            </a>

            {/* Contact Details */}
            <div className="space-y-4">
              <a
                href={`tel:${contact.phone_primary}`}
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                data-testid="contact-phone-1"
              >
                <Phone size={20} className="text-[#FF9933]" />
                <div>
                  <p className="font-semibold text-[#2D2D2D]">{contact.phone_primary}</p>
                  <p className="text-sm text-[#555555]">Primary Contact</p>
                </div>
              </a>
              <a
                href={`tel:${contact.phone_secondary}`}
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                data-testid="contact-phone-2"
              >
                <Phone size={20} className="text-[#93C572]" />
                <div>
                  <p className="font-semibold text-[#2D2D2D]">{contact.phone_secondary}</p>
                  <p className="text-sm text-[#555555]">Secondary Contact</p>
                </div>
              </a>
              <a
                href={contact.google_maps_link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                data-testid="contact-location"
              >
                <MapPin size={20} className="text-[#B76E79]" />
                <div>
                  <p className="font-semibold text-[#2D2D2D]">{contact.address}</p>
                  <p className="text-sm text-[#555555]">View on Google Maps</p>
                </div>
              </a>
              <a
                href={contact.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                data-testid="contact-instagram"
              >
                <Instagram size={20} className="text-[#E4405F]" />
                <div>
                  <p className="font-semibold text-[#2D2D2D]">{contact.instagram_handle}</p>
                  <p className="text-sm text-[#555555]">Follow us on Instagram</p>
                </div>
              </a>
              <a
                href={`mailto:${contact.email || 'utsavanindianbakery@gmail.com'}`}
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                data-testid="contact-email"
              >
                <Mail size={20} className="text-[#FF9933]" />
                <div>
                  <p className="font-semibold text-[#2D2D2D]">{contact.email || 'utsavanindianbakery@gmail.com'}</p>
                  <p className="text-sm text-[#555555]">Email us for inquiries</p>
                </div>
              </a>
            </div>
          </div>

          {/* Opening Hours */}
          <div className="contact-card">
            <h3 className="font-heading text-2xl font-bold text-[#2D2D2D] mb-6 flex items-center gap-3">
              <Clock size={24} className="text-[#FF9933]" />
              Opening Hours
            </h3>
            <div className="space-y-3">
              {days.map((day) => (
                <div
                  key={day}
                  className="flex justify-between items-center py-3 border-b border-gray-100 last:border-0"
                  data-testid={`hours-${day.toLowerCase()}`}
                >
                  <span className="font-medium text-[#2D2D2D]">{day}</span>
                  <span className="text-[#555555]">{contact.opening_hours[day]}</span>
                </div>
              ))}
            </div>

            {/* Note */}
            <div className="mt-6 p-4 bg-[#FF9933]/10 rounded-lg">
              <p className="text-sm text-[#2D2D2D]">
                <span className="font-semibold">Note:</span> For theme cakes, kindly order at least 
                <span className="font-semibold text-[#FF9933]"> 7-10 days</span> before the event.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Section
const Footer = ({ contact }) => {
  return (
    <footer className="footer" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid md:grid-cols-3 gap-8 text-center md:text-left">
          {/* Brand */}
          <div className="flex flex-col items-center md:items-start">
            <div className="flex items-center gap-3 mb-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_0414f390-949e-455d-be81-ddfd0c694b7a/artifacts/qfscq7gd_utsav_an_indian_bakery-20260301-0001.jpg" 
                alt="Utsav Bakery Logo" 
                className="w-14 h-14 rounded-full object-cover border-2 border-[#FF9933]/50"
              />
              <span className="font-accent text-4xl text-[#FF9933]">Utsav</span>
            </div>
            <p className="text-gray-400 mt-2">An Indian Bakery</p>
            <p className="text-gray-400 text-sm mt-4">
              Freshly baked, 100% eggless cakes made with love in Netherlands.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-bold mb-4">Quick Links</h4>
            <div className="space-y-2">
              <a href="#menu" className="block text-gray-400 hover:text-[#FF9933] transition-colors">Menu</a>
              <a href="#about" className="block text-gray-400 hover:text-[#FF9933] transition-colors">About Us</a>
              <a href="#reviews" className="block text-gray-400 hover:text-[#FF9933] transition-colors">Reviews</a>
              <a href="#contact" className="block text-gray-400 hover:text-[#FF9933] transition-colors">Contact</a>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading text-lg font-bold mb-4">Contact</h4>
            <div className="space-y-2 text-gray-400 text-sm">
              <p>{contact.phone_primary}</p>
              <p>{contact.phone_secondary}</p>
              <p>{contact.address}</p>
            </div>
            <div className="flex justify-center md:justify-start gap-4 mt-4">
              <a
                href={contact.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#FF9933] transition-all duration-300 hover:scale-110"
                data-testid="footer-instagram"
              >
                <Instagram size={20} />
              </a>
              <a
                href="https://wa.me/31685177049"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#25D366] transition-all duration-300 hover:scale-110"
                data-testid="footer-whatsapp"
              >
                <MessageCircle size={20} />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-700 mt-8 pt-8 text-center">
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} Utsav - An Indian Bakery. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Floating WhatsApp Button
const WhatsAppFloat = () => {
  return (
    <a
      href="https://wa.me/31685177049?text=Hi! I would like to place an order from Utsav Bakery."
      target="_blank"
      rel="noopener noreferrer"
      className="whatsapp-float"
      data-testid="whatsapp-float"
    >
      <div className="w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform relative">
        <MessageCircle size={28} className="text-white" />
        {/* Pulse ring effect */}
        <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-30"></span>
      </div>
    </a>
  );
};

// Main Home Component
const Home = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [contact, setContact] = useState({
    business_name: "Utsav - An Indian Bakery",
    tagline: "An Indian Bakery",
    owner: "Jayshree",
    address: "Amerikalaan 123, 2408 TX, Alphen aan den Rijn, Netherlands",
    phone_primary: "+31 6 85 17 70 49",
    phone_secondary: "+31 6 13 95 61 20",
    whatsapp_link: "https://wa.me/31685177049",
    instagram: "https://www.instagram.com/utsav_an_indian_bakery",
    instagram_handle: "@utsav_an_indian_bakery",
    google_maps_link: "https://maps.app.goo.gl/bwsMxxKmFdBuJRgJ6",
    rating: 5.0,
    total_reviews: 149,
    opening_hours: {
      Sunday: "9:00 AM - 9:00 PM",
      Monday: "9:00 AM - 9:00 PM",
      Tuesday: "9:00 AM - 9:00 PM",
      Wednesday: "9:00 AM - 9:00 PM",
      Thursday: "9:00 AM - 9:00 PM",
      Friday: "9:00 AM - 9:00 PM",
      Saturday: "9:00 AM - 9:00 PM",
    },
  });
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [menuRes, reviewsRes, contactRes, featuresRes] = await Promise.all([
          axios.get(`${API}/menu`),
          axios.get(`${API}/reviews`),
          axios.get(`${API}/contact`),
          axios.get(`${API}/features`),
        ]);
        setMenuItems(Array.isArray(menuRes.data) ? menuRes.data : []);
        setReviews(Array.isArray(reviewsRes.data) ? reviewsRes.data : []);
        setContact(contactRes.data || {});
        setFeatures(featuresRes.data || {});
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load some data. Please refresh the page.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]" data-testid="loading">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#FF9933] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 font-accent text-2xl text-[#FF9933]">Utsav</p>
          <p className="text-[#555555]">Loading deliciousness...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="home-page">
      <Toaster position="top-center" richColors />
      <Navbar />
      <HeroSection contact={contact} />
      <MenuSection menuItems={menuItems} />
      <AboutSection features={features} />
      <ReviewsSection reviews={reviews} />
      <ContactSection contact={contact} />
      <Footer contact={contact} />
      <WhatsAppFloat />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/menu" element={<MenuPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
